import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import {
  applySnapshot,
  cast,
  detach,
  flow,
  getParentOfType,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import { formatDateToISO, killNulls } from 'utils';
import { AutomationMetersFilterOptions, IObject, ModalModeTypes } from 'types';
import { Automation } from 'http-requests';
import { TaskReportDataStatuses } from 'views/authorized/pages/automation/tabs/automation-result/types';
import { HeatCellPosition } from 'views/authorized/pages/automation/tabs/heat-consumption/constants';
import {
  BodyLine,
  HeatCell,
  HeatCellGroup,
  RawCell,
  ReportCell,
} from 'views/authorized/pages/automation/tabs/heat-consumption/types';
import { ISelectValue } from 'components/ui';

import { RequestStateModel } from './general-models';
import { ConnectionSchemaModel, ExpancesModel } from './metersStore';

const AutomationMetersFilterOptionsType =
  types.enumeration<AutomationMetersFilterOptions>(
    'AutomationMetersFilterOptions',
    ['all', 'unconfigured']
  );

const ModalNamesEnum = types.enumeration('ModalNames', [
  'automationModal',
  'automationRegimeModal',
  'automationBoilerModal',
  'automationBoilerHouseModal',
  'automationBoilerMeterModal',
  'automationSimCardsModal',
  'automationCardModal',
]);

type ModalNames = Instance<typeof ModalNamesEnum>;
export interface IHeatReportBody extends Instance<typeof HeatReportBodyModel> {}
export interface IHeatReportFooter extends Instance<
  typeof HeatReportFooterModel
> {}
export interface IHeatReportRaw extends Instance<typeof HeatReportRawModel> {}
export interface IHeatReportRawCell extends Instance<
  typeof HeatReportRawCellModel
> {}
export interface IHouseReport extends Instance<typeof HouseReportModel> {}
export interface IMeterTask extends Instance<typeof MeterTaskModel> {}
export interface IMeterTaskHistory extends Instance<
  typeof MeterTaskHistoryModel
> {}
export interface IMeterTaskStatusHistory extends Instance<
  typeof MeterTaskStatusHistoryModel
> {}
export interface ISubmitHeatReport extends Instance<
  typeof SubmitHeatReportModel
> {}
export interface IHeatReport extends Instance<typeof HeatReportModel> {}
export interface ISimAdapter extends Instance<typeof SimAdapterModel> {}
export interface IMeterData extends Instance<typeof MeterDataModel> {}
export interface ILastTask extends Instance<typeof LastTaskModel> {}
export interface ILastFormedReportMeter extends Instance<
  typeof LastFormedReportMeterModel
> {}
export interface IHouseBoiler extends Instance<typeof HouseBoilerModel> {}
export interface IBoilerGraphTemp extends Instance<
  typeof BoilerGraphTempModel
> {}
export interface IBoilerGraph extends Instance<typeof BoilerGraphModel> {}
export interface IBoiler extends Instance<typeof BoilerModel> {}
export interface ISimCardModel extends Instance<typeof SimCardModel> {}

const StatusesModel = types.model({
  housesTaskReport: types.optional(RequestStateModel, {}),
  metersReports: types.optional(RequestStateModel, {}),
  heatReport: types.optional(RequestStateModel, {}),
  submitHeatReportList: types.optional(RequestStateModel, {}),
  temperatureCorrection: types.optional(RequestStateModel, {}),
  simAdapters: types.optional(RequestStateModel, {}),
  lastTasks: types.optional(RequestStateModel, {}),
  submitHeatReport: types.optional(RequestStateModel, {}),
  boilers: types.optional(RequestStateModel, {}),
  createBoiler: types.optional(RequestStateModel, {}),
  saveBoiler: types.optional(RequestStateModel, {}),
  manageHouse: types.optional(RequestStateModel, {}),
  updateHouse: types.optional(RequestStateModel, {}),
  businessTypeProviders: types.optional(RequestStateModel, {}),
  defaultSettings: types.optional(RequestStateModel, {}),
  simCards: types.optional(RequestStateModel, {}),
  createSim: types.optional(RequestStateModel, {}),
  updateSim: types.optional(RequestStateModel, {}),
  deleteSim: types.optional(RequestStateModel, {}),
  recalculateMeter: types.optional(RequestStateModel, {}),
  recalculateReportLine: types.optional(RequestStateModel, {}),
  updateReportLine: types.optional(RequestStateModel, {}),
  meltDownReport: types.optional(RequestStateModel, {}),
});

const MeterTaskStatusHistoryModel = types.model({
  status: types.optional(types.string, ''),
  datetime: types.optional(types.string, ''),
});

const MeterTaskHistoryModel = types.model({
  created: types.optional(types.string, ''),
  date_from: types.optional(types.string, ''),
  date_till: types.optional(types.string, ''),
  id: types.optional(types.string, ''),
  is_manual: types.optional(types.maybeNull(types.boolean), null),
  meter: types.optional(types.string, ''),
  report_type: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  status_history: types.optional(types.array(MeterTaskStatusHistoryModel), []),
  warnings: types.optional(types.array(types.string), []),
});

const MeterTaskStatusesModel = types.model({
  meterReport: types.optional(RequestStateModel, {}),
  history: types.optional(RequestStateModel, {}),
});

const MeterTaskModel = types
  .model({
    description: types.optional(types.string, ''),
    id: types.optional(types.string, ''),
    model: types.optional(types.string, ''),
    serial_number: types.optional(types.string, ''),
    responded_status: types.optional(types.string, ''),
    state: types.optional(types.string, ''),
    requestStatuses: types.optional(MeterTaskStatusesModel, {}),
    historyStatuses: types.optional(RequestStateModel, {}),
    history: types.optional(
      types.maybeNull(types.array(MeterTaskHistoryModel)),
      null
    ),
  })
  .views((self) => ({
    get sortedHistoryByDay(): IMeterTaskHistory[][] {
      return Object.values(
        groupBy(self.history, (item: IMeterTaskHistory) => {
          return item.created.split('T')[0];
        })
      );
    },
  }))
  .actions((self) => ({
    setMeterState(value: string) {
      self.state = value;
    },
    getMeterHistory: flow(function* (params?: {
      report_type?: string;
      created?: string;
      limit?: number;
      offset?: number;
    }) {
      try {
        self.historyStatuses.loadingStates();
        const response = yield Automation.GetMeterTaskReport(
          killNulls(Object.assign({ meter: self.id }, params))
        );
        self.history = response.results;
        self.historyStatuses.successStates();

        return response.results;
      } catch (e) {
        self.historyStatuses.errorStates();
        console.error(e);
      } finally {
        self.historyStatuses.finallyStates();
      }
    }),
  }));

const HouseReportModel = types
  .model({
    data: types.optional(types.array(MeterTaskModel), []),
    house: types.optional(types.string, ''),
    requestStatus: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get commonDataRespondedStatus(): TaskReportDataStatuses {
      let successStatusesCounter: number = 0;
      let onlineStatusesCounter: number = 0;
      let offlineStatusesCounter: number = 0;

      self.data.forEach((item): void => {
        if (item.responded_status === 'finished' && item.state === 'on') {
          successStatusesCounter++;
        }

        if (item.state !== 'on') {
          offlineStatusesCounter++;
        } else {
          onlineStatusesCounter++;
        }
      });

      return offlineStatusesCounter === self.data.length
        ? 'offline'
        : successStatusesCounter === onlineStatusesCounter
          ? 'full'
          : !!successStatusesCounter
            ? 'partly'
            : 'none';
    },
  }));

const SubmitHeatReportModel = types.model({
  id: types.optional(types.string, ''),
  meter: types.optional(types.string, ''),
  worker: types.optional(types.maybeNull(types.string), null),
  date_from: types.optional(types.string, ''),
  date_till: types.optional(types.string, ''),
  submit_date: types.optional(types.string, ''),
  temperature: types.optional(types.number, 0),
});

const LastFormedParamsModel = types
  .model({
    meter: types.optional(types.string, ''),
    house: types.optional(types.string, ''),
    report_type: types.optional(types.string, ''),
    date_from: types.optional(types.string, ''),
    date_till: types.optional(types.string, ''),
  })
  .views((self) => ({
    get paramsWithoutHouse(): {
      meter: string;
      report_type: string;
      date_from: string;
      date_till: string;
    } {
      const { house, ...rest } = self;

      return clone(rest);
    },
  }));

const LastFormedReportMeterModel = types.model({
  address: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const HeatReportParamsModel = types
  .model({
    dateFrom: types.optional(types.string, ''),
    dateTill: types.optional(types.string, ''),
    reportType: types.optional(types.string, ''),
    selectedHouse: types.optional(types.string, ''),
    selectedMeter: types.optional(types.string, ''),
    lastSearchSnapshot: types.optional(
      types.frozen<{ [key: string]: any }>(),
      {}
    ),
    lastFormedHeatReportParams: types.optional(LastFormedParamsModel, {}),
    lastFormedSubmitReport: types.optional(SubmitHeatReportModel, {}),
    lastFormedReportMeter: types.optional(LastFormedReportMeterModel, {}),
  })
  .views((self) => ({
    get isParamsEqualLastSearch(): boolean {
      return isEqual(
        {
          dateFrom: self.dateFrom,
          dateTill: self.dateTill,
          reportType: self.reportType,
          selectedMeter: self.selectedMeter,
          selectedHouse: self.selectedHouse,
        },
        self.lastSearchSnapshot // Поскольку в lastFormedHeatReportParams dateTill модернизируется в соответствии с ответом сервера, пришлось создать отдельный объект
      );
    },
    get isValidDatePeriod(): boolean {
      return (
        !!self.dateFrom &&
        !!self.dateTill &&
        Math.abs(moment(self.dateFrom).diff(moment(self.dateTill), 'days')) <=
          (self.reportType === 'daily' ? 31 : 7)
      );
    },
    get isMeterSelected(): boolean {
      return !!self.selectedHouse && !!self.selectedMeter;
    },
    get isValidParams(): boolean {
      return (
        !!self.reportType && this.isMeterSelected && this.isValidDatePeriod
      );
    },
    get isReportDateBeforeLastSubmitDate(): boolean {
      if (
        !!self.lastFormedHeatReportParams.date_from &&
        !!self.lastFormedSubmitReport.date_till
      ) {
        return moment(self.lastFormedHeatReportParams.date_from).isSameOrBefore(
          moment(self.lastFormedSubmitReport.date_till),
          'day'
        );
      }

      return false;
    },
    get isReportDateIsNextDayAfterSubmit(): boolean {
      if (
        !!self.lastFormedHeatReportParams.date_from &&
        !!self.lastFormedSubmitReport.date_till
      ) {
        return moment(self.lastFormedSubmitReport.date_till)
          .add(1, 'day')
          .isSame(moment(self.lastFormedHeatReportParams.date_from), 'day');
      }

      return false;
    },
    get isLastFormedMeter(): boolean {
      return self.selectedMeter === self.lastFormedHeatReportParams.meter;
    },
  }))
  .actions((self) => ({
    setSelectedId(key: 'selectedHouse' | 'selectedMeter', value: string) {
      self[key] = value;
    },
    setReportType(type: string) {
      self.reportType = type;
    },
    setSelectedDate(key: 'dateFrom' | 'dateTill', ISODate: string) {
      self[key] = ISODate;
    },
    setLastFormedHeatReportParams(params: IObject) {
      self.lastFormedHeatReportParams = cast(params);
    },
    setLastFormedSubmitReport(report: ISubmitHeatReport) {
      self.lastFormedSubmitReport = report;
    },
    setLastFormedReportMeter(meter: ILastFormedReportMeter) {
      self.lastFormedReportMeter = meter;
    },
    setLastSearchSnapshot(value: IObject) {
      self.lastSearchSnapshot = value;
    },
  }));

const BoilerCaptionModel = types.model({
  name: types.optional(types.string, ''),
  region_code: types.optional(types.string, ''),
});

const HouseContractCaptionModel = types.model({
  doc: types.optional(types.string, ''),
  inspector: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  worker: types.optional(types.string, ''),
});

const HouseCaptionModel = types.model({
  address: types.optional(types.string, ''),
  heating_contract: types.optional(HouseContractCaptionModel, {}),
});

const HeatReportCaptionModel = types.model({
  boiler: types.optional(BoilerCaptionModel, {}),
  connection_schema: types.optional(ConnectionSchemaModel, {}),
  flowmeter_type: types.optional(types.maybeNull(types.string), null),
  house: types.optional(HouseCaptionModel, {}),
  house_address: types.optional(types.string, ''),
  meter: types.optional(types.frozen<{ [key: string]: any }>(), {}),
  provider: types.optional(types.string, ''),
  provider_phones: types.optional(types.string, ''),
  report_created_at: types.optional(types.string, ''),
  report_name: types.optional(types.string, ''),
  title: types.optional(types.string, ''),
  worker: types.optional(types.string, ''),
});

const HeatReportBodyModel = types
  .model({
    // Сводная информация
    id: types.optional(types.string, ''),
    meter: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    datetime: types.optional(types.string, ''),
    lock: types.optional(types.boolean, false),
    error: types.optional(types.boolean, false),
    error_messages: types.optional(types.array(types.string), []),
    correction: types.optional(types.string, ''),
    // Поля строки отчета
    NS: types.optional(types.maybeNull(types.number), null),
    M1: types.optional(types.number, 0),
    M2: types.optional(types.number, 0),
    M3: types.optional(types.number, 0),
    M4: types.optional(types.number, 0),
    P1: types.optional(types.number, 0),
    P2: types.optional(types.number, 0),
    Q: types.optional(types.number, 0),
    Q_1: types.optional(types.number, 0),
    Q_1_control: types.optional(types.number, 0),
    Q_1_delta: types.optional(types.number, 0),
    Q_2: types.optional(types.number, 0),
    Q_2_control: types.optional(types.number, 0),
    Q_2_delta: types.optional(types.number, 0),
    Q_control: types.optional(types.number, 0),
    Q_delta: types.optional(types.number, 0),
    Qg: types.optional(types.number, 0),
    Qo: types.optional(types.number, 0),
    Qo_control: types.optional(types.number, 0),
    Qo_delta: types.optional(types.number, 0),
    T1: types.optional(types.number, 0),
    T2: types.optional(types.number, 0),
    T3: types.optional(types.number, 0),
    T4: types.optional(types.number, 0),
    V3: types.optional(types.number, 0),
    V3_control: types.optional(types.number, 0),
    V3_delta: types.optional(types.number, 0),
    V4: types.optional(types.number, 0),
    V4_control: types.optional(types.number, 0),
    V4_delta: types.optional(types.number, 0),
    Vp: types.optional(types.maybeNull(types.number), null),
    dM_1: types.optional(types.number, 0),
    dM_1_control: types.optional(types.number, 0),
    dM_1_delta: types.optional(types.number, 0),
    dM_2: types.optional(types.number, 0),
    dM_2_control: types.optional(types.number, 0),
    dM_2_delta: types.optional(types.number, 0),
    dT: types.optional(types.number, 0),
    dV: types.optional(types.number, 0),
    dV_control: types.optional(types.number, 0),
    dV_delta: types.optional(types.number, 0),
    time: types.optional(types.maybeNull(types.string), null),
  })
  .preProcessSnapshot((self) => {
    return {
      ...self,
      time: self?.time?.toString(),
    };
  });

const HeatReportFooterAvgModel = types.model({
  M1: types.optional(types.number, 0),
  M2: types.optional(types.number, 0),
  M3: types.optional(types.number, 0),
  M4: types.optional(types.number, 0),
  P1: types.optional(types.number, 0),
  P2: types.optional(types.number, 0),
  Q: types.optional(types.number, 0),
  Q_1: types.optional(types.number, 0),
  Q_2: types.optional(types.number, 0),
  Qg: types.optional(types.number, 0),
  Qo: types.optional(types.number, 0),
  T1: types.optional(types.number, 0),
  T2: types.optional(types.number, 0),
  T3: types.optional(types.number, 0),
  T4: types.optional(types.number, 0),
  V3: types.optional(types.number, 0),
  V4: types.optional(types.number, 0),
  Vp: types.optional(types.number, 0),
  dM_1: types.optional(types.number, 0),
  dM_2: types.optional(types.number, 0),
  dT: types.optional(types.number, 0),
  dV: types.optional(types.number, 0),
  NS: types.optional(types.string, ''),
  time: types.optional(types.number, 0),
  datetime: types.optional(types.string, ''),
});

const HeatReportFooterDataModel = types.model({
  M1: types.optional(types.number, 0),
  M2: types.optional(types.number, 0),
  M3: types.optional(types.number, 0),
  M4: types.optional(types.number, 0),
  Q: types.optional(types.number, 0),
  V3: types.optional(types.number, 0),
  V4: types.optional(types.number, 0),
  time: types.optional(types.maybeNull(types.number), 0),
  _date: types.optional(types.string, ''),
});

const HeatReportFooterSumModel = types.model({
  M1: types.optional(types.number, 0),
  M2: types.optional(types.number, 0),
  M3: types.optional(types.number, 0),
  M4: types.optional(types.number, 0),
  Q: types.optional(types.number, 0),
  Q_1: types.optional(types.number, 0),
  Q_2: types.optional(types.number, 0),
  Qg: types.optional(types.number, 0),
  Qo: types.optional(types.number, 0),
  V3: types.optional(types.number, 0),
  V4: types.optional(types.number, 0),
  Vp: types.optional(types.number, 0),
  dM_1: types.optional(types.number, 0),
  dM_2: types.optional(types.number, 0),
  dV: types.optional(types.number, 0),
  time: types.optional(types.number, 0),
  T1: types.optional(types.maybeNull(types.number), null),
  T2: types.optional(types.maybeNull(types.number), null),
  dT: types.optional(types.maybeNull(types.number), null),
  P1: types.optional(types.maybeNull(types.number), null),
  P2: types.optional(types.maybeNull(types.number), null),
  T3: types.optional(types.maybeNull(types.number), null),
  T4: types.optional(types.maybeNull(types.number), null),
  NS: types.optional(types.string, ''),
  datetime: types.optional(types.string, ''),
});

const HeatReportFooterModel = types.model({
  average: types.optional(HeatReportFooterAvgModel, {}),
  in_data: types.optional(HeatReportFooterDataModel, {}),
  out_data: types.optional(HeatReportFooterDataModel, {}),
  sum: types.optional(HeatReportFooterSumModel, {}),
});

const HeatReportRawCellModel = types.model({
  value: types.optional(types.number, 0),
  unit: types.optional(types.string, ''),
});

const HeatReportRawModel = types.model({
  M1_1: types.optional(HeatReportRawCellModel, {}),
  M1_2: types.optional(HeatReportRawCellModel, {}),
  M2_1: types.optional(HeatReportRawCellModel, {}),
  M2_2: types.optional(HeatReportRawCellModel, {}),
  M3_1: types.optional(HeatReportRawCellModel, {}),
  M3_2: types.optional(HeatReportRawCellModel, {}),
  NS0_1: types.optional(HeatReportRawCellModel, {}),
  NS0_2: types.optional(HeatReportRawCellModel, {}),
  NS1_1: types.optional(HeatReportRawCellModel, {}),
  NS1_2: types.optional(HeatReportRawCellModel, {}),
  NS2_1: types.optional(HeatReportRawCellModel, {}),
  NS2_2: types.optional(HeatReportRawCellModel, {}),
  P1_1: types.optional(HeatReportRawCellModel, {}),
  P1_2: types.optional(HeatReportRawCellModel, {}),
  P2_1: types.optional(HeatReportRawCellModel, {}),
  P2_2: types.optional(HeatReportRawCellModel, {}),
  Q_1: types.optional(HeatReportRawCellModel, {}),
  Q_2: types.optional(HeatReportRawCellModel, {}),
  Qg_1: types.optional(HeatReportRawCellModel, {}),
  Qg_2: types.optional(HeatReportRawCellModel, {}),
  SP_1: types.optional(HeatReportRawCellModel, {}),
  SP_2: types.optional(HeatReportRawCellModel, {}),
  Ti_1: types.optional(HeatReportRawCellModel, {}),
  Ti_2: types.optional(HeatReportRawCellModel, {}),
  V1_1: types.optional(HeatReportRawCellModel, {}),
  V1_2: types.optional(HeatReportRawCellModel, {}),
  V2_1: types.optional(HeatReportRawCellModel, {}),
  V2_2: types.optional(HeatReportRawCellModel, {}),
  V3_1: types.optional(HeatReportRawCellModel, {}),
  V3_2: types.optional(HeatReportRawCellModel, {}),
  t1_1: types.optional(HeatReportRawCellModel, {}),
  t1_2: types.optional(HeatReportRawCellModel, {}),
  t2_1: types.optional(HeatReportRawCellModel, {}),
  t2_2: types.optional(HeatReportRawCellModel, {}),
  t3_1: types.optional(HeatReportRawCellModel, {}),
  t3_2: types.optional(HeatReportRawCellModel, {}),
  th_1: types.optional(HeatReportRawCellModel, {}),
  th_2: types.optional(HeatReportRawCellModel, {}),
  tv_1: types.optional(HeatReportRawCellModel, {}),
  tv_2: types.optional(HeatReportRawCellModel, {}),
});

/* caption - заголовок отчета, body - тело отчета, footer - подвал отчета,
    raw - сырые данные опроса прибора учета */
const HeatReportModel = types
  .model({
    caption: types.optional(HeatReportCaptionModel, {}),
    body: types.optional(types.array(HeatReportBodyModel), []),
    footer: types.optional(
      types.frozen<{ [key: string]: IHeatReportFooter }>(),
      {}
    ),
    raw: types.optional(types.array(HeatReportRawModel), []),
  })
  .views((self) => ({
    get isReportGenerated(): boolean {
      return !!self.raw.length && !!self.body.length && !!self.footer.length;
    },
    get reportFooter(): IHeatReportFooter {
      return self.footer[Object.keys(self.footer)[0]];
    },
    get reportRows() {
      if (this.isReportGenerated) {
        const body: BodyLine[] = self.body.map((line: IHeatReportBody) => {
          return {
            id: line.id,
            error: line.error,
            errorMessages: line.error_messages,
            lock: line.lock,
            cells: HeatCellPosition.map((group: HeatCellGroup) =>
              group.cells.map((cell: HeatCell) => {
                return { name: cell, value: line[cell] };
              })
            ),
          };
        });

        const average: ReportCell[][] = HeatCellPosition.map(
          (group: HeatCellGroup) =>
            group.cells.map((cell: HeatCell) => {
              return {
                name: cell,
                value: this.reportFooter.average[cell],
              };
            })
        );

        const sum: ReportCell[][] = HeatCellPosition.map(
          (group: HeatCellGroup) =>
            group.cells.map((cell: HeatCell) => {
              return {
                name: cell,
                value: this.reportFooter.sum[cell],
              };
            })
        );

        return { body, average, sum };
      }

      return { body: [], average: [], sum: [] };
    },
    get rawRows(): RawCell[][] {
      if (this.isReportGenerated) {
        const formattedRows = self.raw.map((line: IHeatReportRaw) =>
          Object.entries(line).map((cell: [string, IHeatReportRawCell]) => {
            return {
              title: cell[0],
              subTitle: cell[1].unit,
              value: cell[1].value.toFixed(5),
            };
          })
        );

        return formattedRows.map((line: RawCell[], lineIndex: number) =>
          union(
            [
              {
                title: 'Дата',
                subTitle: '',
                value: moment(self.body[lineIndex].datetime).format('DD.MM.yy'),
                isFirstInGroup: false,
              },
            ],
            line.map((cell: RawCell, cellIndex: number) =>
              Object.assign(cell, {
                isFirstInGroup:
                  !!cellIndex &&
                  formattedRows[lineIndex][cellIndex - 1].subTitle.charAt(0) !==
                    cell.subTitle.charAt(0),
              })
            )
          )
        );
      }

      return [];
    },
    get reportType(): string {
      return getParentOfType(self, AutomationStore).heatReportParams
        .reportType as string;
    },
  }));

const HeatingContractModel = types
  .model({
    id: types.optional(types.string, ''),
    boiler: types.optional(types.maybeNull(types.string), null),
    number: types.optional(types.maybeNull(types.string), null),
    date: types.optional(types.maybeNull(types.string), null),
    expances: types.optional(types.maybeNull(ExpancesModel), null),
    worker: types.optional(types.maybeNull(types.string), null),
    inspector: types.optional(types.maybeNull(types.string), null),
    doc: types.optional(types.maybeNull(types.string), null),
  })
  .volatile(() => ({
    previousBoiler: null as string | null,
  }))
  .actions((self) => ({
    afterCreate() {
      self.previousBoiler = self.boiler;
    },
    setBoilerId(boilerId: string) {
      self.boiler = boilerId;
    },
    setNumber(value: string) {
      self.number = value;
    },
    setDate(value: string) {
      self.date = value;
    },
  }));

const AutomationSettingsModel = types
  .model({
    auto_daily: types.optional(types.boolean, false),
    auto_hourly: types.optional(types.boolean, false),
    auto_total: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

const HouseBoilerModel = types
  .model('HouseBoilerModel', {
    id: types.optional(types.string, ''),
    address: types.optional(types.string, ''),
    heating_contract: types.optional(HeatingContractModel, {}),
    automation_settings: types.optional(AutomationSettingsModel, {}),
  })
  .preProcessSnapshot((snapshot) => {
    return {
      ...snapshot,
      automation_settings: {
        auto_daily: snapshot.automation_settings?.auto_daily ?? false,
        auto_hourly: snapshot.automation_settings?.auto_hourly ?? false,
        auto_total: snapshot.automation_settings?.auto_total ?? false,
      },
    };
  })
  .volatile((self) => ({
    initialSnapshot: null as null | typeof self,
  }))
  .views((self) => ({
    get isModified(): boolean {
      return (
        !!self.initialSnapshot &&
        !isEqual(getSnapshot(self), self.initialSnapshot)
      );
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.initialSnapshot = getSnapshot(cast(self));
    },
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

const BoilerGraphTempModel = types
  .model({
    graph: types.optional(types.array(types.string), []),
    header: types.optional(types.string, ''),
    values: types.optional(types.array(types.number), []),
  })
  .actions((self) => ({
    setValues(value: number, index: number) {
      self.values[index] = value;
    },
  }));

const BoilerGraphModel = types
  .model('BoilerGraphModel', {
    name: types.optional(types.string, ''),
    comment: types.optional(types.string, ''),
    period: types.optional(types.array(types.string), [
      moment().toISOString(true),
      moment().toISOString(true),
    ]),
    t0: types.optional(BoilerGraphTempModel, {}),
    tv1: types.optional(types.array(BoilerGraphTempModel), []),
    tv2: types.optional(types.array(BoilerGraphTempModel), []),
    created_at: types.optional(types.maybeNull(types.string), null),
  })
  .volatile(() => ({
    mappedGraph: null as null | Map<
      number,
      {
        tv1: number[];
        tv2: number[];
      }
    >,
  }))
  .views((self) => ({
    get graphToMap() {
      const map = new Map<number, { tv1: number[]; tv2: number[] }>();
      self.t0.values.forEach((t0Value, index) => {
        map.set(t0Value, {
          tv1: self.tv1.map((tv1Item) => tv1Item.values[index] ?? 0),
          tv2: self.tv2.map((tv2Item) => tv2Item.values[index] ?? 0),
        });
      });

      return map;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.mappedGraph = self.graphToMap;
    },
    setStringProperties(key: 'name' | 'comment', value: string) {
      self[key] = value;
    },
    setPeriod(period: [string, string] | []) {
      self.period = cast(period);
    },
    editT0Values(place: 'top' | 'bottom', method: 'add' | 'delete') {
      const updateValues = (
        values: number[],
        place: 'top' | 'bottom',
        method: 'add' | 'delete',
        isT0: boolean = false
      ) => {
        if (method === 'add') {
          if (place === 'top') {
            if (isT0) {
              const maxVal = values.length === 0 ? 0 : Math.max(...values);
              values.unshift(maxVal + 1);
            } else {
              values.unshift(0);
            }
          } else {
            if (isT0) {
              const minVal = values.length === 0 ? 0 : Math.min(...values);
              values.push(minVal - 1);
            } else {
              values.push(0);
            }
          }
        } else {
          if (place === 'top') {
            values.shift();
          } else {
            values.pop();
          }
        }
      };

      updateValues(self.t0.values, place, method, true);

      [self.tv1, self.tv2].forEach((tv: IBoilerGraphTemp[]) => {
        tv.forEach((obj: IBoilerGraphTemp) => {
          updateValues(obj.values, place, method);
        });
      });
    },
    setTV(key: 'tv1' | 'tv2', newTV: IBoilerGraphTemp[]) {
      self[key] = cast(newTV);
    },
  }));

const BoilerModel = types
  .model('BoilerModel', {
    id: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    provider: types.optional(types.string, ''),
    region_code: types.optional(types.string, '0'),
    provider_corrections: types.optional(types.array(types.frozen()), []),
    default_corrections: types.optional(types.array(types.frozen()), []),
    graphs: types.optional(types.array(BoilerGraphModel), [
      {
        t0: {
          graph: [],
          header: 'Tнв',
          values: Array.from({ length: 36 }, (_, i) => 10 - i),
        },
        tv1: [
          {
            graph: ['150'],
            header: 'T1',
            values: Array.from({ length: 36 }, () => 0),
          },
          {
            graph: ['70'],
            header: 'T2',
            values: Array.from({ length: 36 }, () => 0),
          },
        ],
      },
    ]),
    houses: types.optional(types.array(HouseBoilerModel), []),
  })
  .volatile((self) => ({
    initialSnapshot: null as null | typeof self,
    actualGraph: null as null | string, // created_at актуального графика
    selectedGraph: null as null | string, // За уникальность берется поле created_at
  }))
  .views((self) => ({
    get isModified(): boolean {
      return (
        !!self.initialSnapshot &&
        !isEqual(getSnapshot(self), self.initialSnapshot)
      );
    },
    get isSaveDisabled(): boolean {
      return (
        !self.name ||
        !self.provider ||
        !self.graphs[0]?.name ||
        self.graphs[0]?.period.some((el) => !el) ||
        !this.isModified
      );
    },
    get currentGraph(): IBoilerGraph {
      return find(self.graphs, { created_at: self.selectedGraph }) || cast({});
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.initialSnapshot = getSnapshot(cast(self));
      self.actualGraph = self.graphs[0].created_at;
      self.selectedGraph = self.graphs[0].created_at;
    },
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
    setGraphs(newGraphs: IBoilerGraph[]) {
      self.graphs = cast(newGraphs);
    },
    toggleCircuitCount() {
      self.graphs[0].tv2 = cast(
        !!self.graphs[0].tv2.length
          ? []
          : [
              {
                graph: ['150'],
                header: 'T1',
                values: Array.from({ length: 36 }, () => 0),
              },
              {
                graph: ['70'],
                header: 'T2',
                values: Array.from({ length: 36 }, () => 0),
              },
            ]
      );
    },
  }));

const SimCardModel = types
  .model('SimCardModel', {
    id: types.optional(types.string, ''),
    is_enabled: types.optional(types.maybeNull(types.boolean), null),
    imsi: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
    minutes_used: types.optional(types.frozen(), {}),
    provider: types.optional(types.string, ''),
    phone_number: types.optional(types.string, ''),
    last_seen: types.optional(types.array(types.string), []),
    last_seen_ok: types.optional(types.boolean, false),
    ussd_command: types.optional(types.maybeNull(types.string), null),
    ussd_result: types.optional(types.maybeNull(types.string), null),
    minutes_monthly_limit: types.optional(types.maybeNull(types.number), null),
    daily_limit_minutes: types.optional(types.maybeNull(types.number), null),
    daily_limit_is_per_second: types.optional(
      types.maybeNull(types.boolean),
      null
    ),
  })
  .preProcessSnapshot((snapshot) => {
    return {
      ...snapshot,
      is_enabled: snapshot.is_enabled ?? true,
      ussd_command: snapshot.ussd_command ?? '*100#',
      minutes_monthly_limit: snapshot.minutes_monthly_limit ?? 0,
      daily_limit_minutes: snapshot.daily_limit_minutes ?? 0,
      daily_limit_is_per_second: snapshot.daily_limit_is_per_second ?? false,
    };
  })
  .volatile((self) => ({
    initialSnapshot: null as null | typeof self,
  }))
  .views((self) => ({
    get isModified(): boolean {
      return (
        !!self.initialSnapshot &&
        !isEqual(getSnapshot(self), self.initialSnapshot)
      );
    },
    get isSaveDisabled(): boolean {
      return (
        !self.imsi ||
        self.imsi.length !== 15 ||
        !self.phone_number ||
        self.phone_number.length !== 11 ||
        !self.ussd_command ||
        !this.isModified
      );
    },
    getLastMinutesUsed(by: 'month' | 'day'): number {
      const currentYear = self.minutes_used[moment().year()];

      if (by === 'month' && !!currentYear) {
        return currentYear[moment().month() + 1] || 0;
      }

      return 0;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.initialSnapshot = getSnapshot(cast(self));
    },
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

const DefaultSettingsModel = types.model('DefaultSettingsModel', {
  sensors: types.optional(types.frozen(), {}),
  mappings: types.optional(types.frozen(), {}),
  calc_formulas: types.optional(types.frozen(), {}),
  default_settings: types.optional(types.frozen(), {}),
  default_mappings: types.optional(types.frozen(), []),
});

const ResourcesSettingsFilterModel = types
  .model('ResourcesSettingsFilterModel', {
    selectedBoiler: types.optional(types.string, ''),
    selectedHouse: types.optional(types.string, ''),
    selectedMetersFilter: types.optional(
      AutomationMetersFilterOptionsType,
      'all'
    ),
  })
  .volatile(() => ({
    isShowArchiveMeters: false,
  }))
  .views((self) => ({
    get resetVisibility() {
      return (
        !!self.selectedBoiler ||
        !!self.selectedHouse ||
        self.selectedMetersFilter !== 'all' ||
        self.isShowArchiveMeters
      );
    },
  }))
  .actions((self) => ({
    setSelectedElement(key: 'selectedBoiler' | 'selectedHouse', value: string) {
      self[key] = value;
    },
    setSelectedMetersFilter(value: AutomationMetersFilterOptions) {
      self.selectedMetersFilter = value;
    },
    toggleIsShowArchiveMeters() {
      self.isShowArchiveMeters = !self.isShowArchiveMeters;
    },
    reset() {
      applySnapshot(self, {});
      self.isShowArchiveMeters = false;
    },
  }));

const ResourcesSettingsModel = types
  .model('ResourcesSettingsModel', {
    statuses: types.optional(StatusesModel, {}),
    filters: types.optional(ResourcesSettingsFilterModel, {}),
    boilers: types.optional(types.array(BoilerModel), []),
    simCardsList: types.optional(types.array(SimCardModel), []),
    editableBoiler: types.optional(BoilerModel, {}),
    editableHouse: types.optional(HouseBoilerModel, {}),
    editableSimCard: types.optional(SimCardModel, {}),
    default_settings: types.optional(DefaultSettingsModel, {}),
  })
  .volatile(() => ({
    modalMode: null as ModalModeTypes | null,
  }))
  .views((self) => ({
    get currentBoiler(): IBoiler | null {
      return (
        self.boilers.find(
          (boiler) => boiler.id === self.editableHouse.heating_contract.boiler
        ) || null
      );
    },
    get previousBoiler(): IBoiler | null {
      return (
        self.boilers.find(
          (boiler) =>
            boiler.id === self.editableHouse.heating_contract.previousBoiler
        ) || null
      );
    },
    get houseToMove(): IHouseBoiler | null {
      return (
        this.previousBoiler?.houses.find(
          (house) => house.id === self.editableHouse.id
        ) || null
      );
    },
    get houseToDelete(): IHouseBoiler | null {
      return (
        this.currentBoiler?.houses.find(
          (house) => house.id === self.editableHouse.id
        ) || null
      );
    },
    get simCardToDelete(): ISimCardModel | null {
      return (
        self.simCardsList.find((card) => card.id === self.editableSimCard.id) ||
        null
      );
    },
    get filteredBoilersList(): IBoiler[] {
      if (!self.filters.selectedBoiler && !self.filters.selectedHouse) {
        return self.boilers;
      }

      let filteredList: IBoiler[] = cloneDeep(self.boilers);

      if (!!self.filters.selectedBoiler) {
        filteredList = filteredList.filter(
          (boiler) => boiler.id === self.filters.selectedBoiler
        );
      }

      if (!!self.filters.selectedHouse) {
        filteredList = filteredList.filter((boiler) => {
          boiler.houses = cast(
            boiler.houses.filter(
              (house) => house.id === self.filters.selectedHouse
            )
          );

          return !!boiler.houses.length;
        });
      }

      return filteredList;
    },
    get boilersOptions(): ISelectValue[] {
      return self.boilers.map((boiler: IBoiler) => ({
        label: boiler.name,
        value: boiler.id,
      }));
    },
    get housesOptions(): ISelectValue[] {
      const allHouses: IHouseBoiler[] = self.boilers
        .flatMap((boiler: IBoiler) => [...boiler.houses])
        .flat();

      return allHouses
        .filter(
          (house: IHouseBoiler) =>
            !self.filters.selectedBoiler ||
            house.heating_contract.boiler === self.filters.selectedBoiler
        )
        .map((house: IHouseBoiler) => ({
          label: house.address,
          value: house.id,
        }));
    },
  }))
  .actions((self) => ({
    startEditingBoiler(boiler?: IBoiler) {
      self.editableBoiler = BoilerModel.create(
        (!!boiler && cloneDeep(boiler)) || {}
      );
    },
    addBoilerToStore(boilerId: string) {
      self.editableBoiler.id = boilerId;
      self.boilers.push(detach(self.editableBoiler));
      self.editableBoiler = cast({});
    },
    updateBoilerInStore() {
      const index = self.boilers.findIndex(
        (boiler) => boiler.id === self.editableBoiler?.id
      );

      if (index !== -1) {
        self.boilers[index] = BoilerModel.create(detach(self.editableBoiler));
      }

      self.editableBoiler = cast({});
    },
    startEditingHouse(
      mode: 'add_house' | 'settings_house',
      house?: IHouseBoiler
    ) {
      self.modalMode = mode;
      self.editableHouse = HouseBoilerModel.create(
        (!!house && cloneDeep(house)) || {}
      );
    },
    addHouseToBoiler() {
      if (!!self.currentBoiler) {
        self.currentBoiler.houses.push(detach(self.editableHouse));
        self.editableHouse = cast({});
      }
    },
    deleteEditingHouse() {
      if (!!self.currentBoiler && !!self.houseToDelete) {
        self.currentBoiler.houses.remove(self.houseToDelete);
        self.editableHouse = cast({});
      }
    },
    updateHouseInStore() {
      if (!!self.previousBoiler && !!self.currentBoiler && !!self.houseToMove) {
        self.previousBoiler.houses.remove(self.houseToMove);
        self.currentBoiler.houses.push(cloneDeep(detach(self.editableHouse)));
        self.editableHouse = HouseBoilerModel.create({});
        self.editableHouse.heating_contract.previousBoiler = null;
      }
    },
    startEditingSimCard(simCard?: ISimCardModel) {
      self.editableSimCard = SimCardModel.create(
        (!!simCard && cloneDeep(simCard)) || {}
      );
    },
    addSimCardToStore(simCardId: string) {
      self.editableSimCard.id = simCardId;
      self.simCardsList.push(detach(self.editableSimCard));
      self.editableSimCard = cast({});
    },
    updateSimCardInStore() {
      const cardIndex = self.simCardsList.findIndex(
        (card) => card.id === self.editableSimCard.id
      );

      if (cardIndex !== -1) {
        self.simCardsList[cardIndex] = detach(self.editableSimCard);
      }

      self.editableSimCard = cast({});
    },
    deleteSimCardFromStore() {
      if (!!self.simCardToDelete) {
        self.simCardsList.remove(self.simCardToDelete);
        self.editableSimCard = cast({});
      }
    },
  }))
  .actions((self) => ({
    setModalMode(mode: ModalModeTypes | null) {
      self.modalMode = mode;
    },
    getBoilers: flow(function* () {
      try {
        self.statuses.boilers.loadingStates();
        const response: IObject = yield Automation.GetBoilers();

        self.boilers = response.data.map((item: IBoiler) =>
          BoilerModel.create(item)
        );
        self.statuses.boilers.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.boilers.errorStates();
      } finally {
        self.statuses.boilers.finallyStates();
      }
    }),
    getDefaultSettings: flow(function* () {
      try {
        self.statuses.defaultSettings.loadingStates();
        const response = yield Automation.GetDefaultSettings();

        self.default_settings = response;
        self.statuses.defaultSettings.successStates();

        return response;
      } catch (e) {
        self.statuses.defaultSettings.errorStates();
        console.error(e);
      } finally {
        self.statuses.defaultSettings.finallyStates();
      }
    }),
    createBoiler: flow(function* () {
      try {
        self.statuses.createBoiler.loadingStates();
        const response = yield Automation.CreateBoilers(self.editableBoiler);

        self.addBoilerToStore(response.boiler_id);
        self.statuses.createBoiler.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.createBoiler.errorStates();

        throw e;
      } finally {
        self.statuses.createBoiler.finallyStates();
      }
    }),
    saveBoiler: flow(function* () {
      try {
        self.statuses.saveBoiler.loadingStates();
        const updatedBoiler = {
          ...self.editableBoiler,
          houses: self.editableBoiler.houses.map((house) => ({
            id: house.id,
            address: house.address,
          })),
        };

        const response = yield Automation.PatchBoiler(
          updatedBoiler.id,
          updatedBoiler
        );

        self.updateBoilerInStore();
        self.statuses.saveBoiler.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.saveBoiler.errorStates();

        throw e;
      } finally {
        self.statuses.saveBoiler.finallyStates();
      }
    }),
    manageHouse: flow(function* (method: 'add' | 'delete') {
      try {
        self.statuses.manageHouse.loadingStates();
        const response = yield Automation.ManageHouse(
          self.editableHouse.heating_contract.boiler || '',
          {
            id: self.editableHouse.id,
            address: self.editableHouse.address,
            number: self.editableHouse.heating_contract.number,
            date: self.editableHouse.heating_contract.date,
            method,
          }
        );

        method === 'add' ? self.addHouseToBoiler() : self.deleteEditingHouse();
        self.statuses.manageHouse.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.manageHouse.errorStates();

        throw e;
      } finally {
        self.statuses.manageHouse.finallyStates();
      }
    }),
    updateHouse: flow(function* () {
      try {
        self.statuses.updateHouse.loadingStates();
        const response = yield Automation.UpdateHouse(self.editableHouse.id, {
          heating_contract: self.editableHouse.heating_contract,
          automation_settings: self.editableHouse.automation_settings,
        });

        self.updateHouseInStore();
        self.statuses.updateHouse.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.updateHouse.errorStates();

        throw e;
      } finally {
        self.statuses.updateHouse.finallyStates();
      }
    }),
    getSimProviders: flow(function* () {
      try {
        self.statuses.simCards.loadingStates();
        const response: IObject = yield Automation.GetSimProviders();

        self.simCardsList = response.data;
        self.statuses.simCards.successStates();

        return response;
      } catch (e) {
        self.statuses.simCards.errorStates();
        console.error(e);
      } finally {
        self.statuses.simCards.finallyStates();
      }
    }),
    createSimCard: flow(function* () {
      try {
        self.statuses.createSim.loadingStates();
        const response = yield Automation.CreateSimCard(self.editableSimCard);

        self.addSimCardToStore(response.sim_id.id);
        self.statuses.createSim.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.createSim.errorStates();

        throw e;
      } finally {
        self.statuses.createSim.finallyStates();
      }
    }),
    updateSimCard: flow(function* () {
      try {
        self.statuses.updateSim.loadingStates();
        const response = yield Automation.UpdateSimCard(
          self.editableSimCard.id,
          self.editableSimCard
        );

        self.updateSimCardInStore();
        self.statuses.updateSim.successStates();

        return response;
      } catch (e) {
        self.statuses.updateSim.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.updateSim.finallyStates();
      }
    }),
    deleteSimCard: flow(function* () {
      try {
        self.statuses.deleteSim.loadingStates();
        const response = yield Automation.DeleteSimCard(
          self.editableSimCard.id
        );

        self.deleteSimCardFromStore();
        self.statuses.deleteSim.successStates();

        return response;
      } catch (e) {
        self.statuses.deleteSim.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.deleteSim.finallyStates();
      }
    }),
  }));

const SimAdapterModel = types.model({
  meter: types.optional(types.string, ''),
  sim_number: types.optional(types.string, ''),
});

const LastTaskModel = types.model({
  is_final: types.optional(types.boolean, false),
  status: types.optional(types.maybeNull(types.string), null),
  task: types.optional(types.maybeNull(types.string), null),
});

const MeterDataModel = types.model({
  last_checkout: types.optional(
    types.frozen<{ [key: string]: string | null }>(),
    {}
  ),
  last_tasks: types.optional(types.frozen<{ [key: string]: ILastTask }>(), {}),
});

const TempCorrectionModel = types
  .model({
    comment: types.optional(types.maybeNull(types.string), null),
    date: types.optional(types.string, ''),
    value: types.optional(types.number, 0),
  })
  .actions((self) => ({
    setValue(value: number) {
      self.value = value;
    },
  }));

const RequestHistoryFiltersModel = types
  .model('RequestHistoryFiltersModel', {
    meter: types.optional(types.string, ''),
    report_type: types.optional(types.string, ''),
    date: types.optional(types.string, ''),
  })
  .views((self) => ({
    get isValidFilters() {
      return !!self.meter && !!self.report_type && !!self.date;
    },
    get historySearchParams() {
      return {
        meter: self.meter,
        date_from__gte: formatDateToISO(moment(self.date).startOf('month')),
        date_till__lte: formatDateToISO(moment(self.date).endOf('month')),
        created: 'include',
      };
    },
    get selectedMonthYear(): [number, number] {
      return [moment(self.date).get('month'), moment(self.date).get('year')];
    },
    get selectedDay(): number {
      return moment(self.date).get('date');
    },
  }))
  .actions((self) => ({
    setFilter(key: 'meter' | 'report_type' | 'date', value: string) {
      self[key] = value;
    },
  }));

const RequestHistoryModel = types
  .model('RequestHistoryModel', {
    filters: types.optional(RequestHistoryFiltersModel, {}),
    requestsList: types.optional(types.array(MeterTaskHistoryModel), []),
    requestsListStatus: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get currentTypeRequestList(): IMeterTaskHistory[] {
      return self.requestsList.filter(
        (request: IMeterTaskHistory) =>
          request.report_type === self.filters.report_type
      );
    },
    get hourlyRequestList(): IMeterTaskHistory[] {
      return self.requestsList.filter(
        (request: IMeterTaskHistory) => request.report_type === 'hourly'
      );
    },
    get currentDayRequestsList(): IMeterTaskHistory[] {
      return this.currentTypeRequestList.filter((request: IMeterTaskHistory) =>
        moment(self.filters.date).isSame(moment(request.created), 'day')
      );
    },
  }))
  .actions((self) => ({
    clearModel() {
      applySnapshot(self, {});
    },
    getMeterHistory: flow(function* () {
      try {
        self.requestsListStatus.loadingStates();
        const response: IObject = yield Automation.GetMeterTaskReport(
          self.filters.historySearchParams
        );
        self.requestsList = response.results;
        self.requestsListStatus.successStates();

        return response.results;
      } catch (e) {
        self.requestsListStatus.errorStates();
        console.error(e);
      } finally {
        self.requestsListStatus.finallyStates();
      }
    }),
  }));

const MeterActualStatusModel = types.model('MeterActualStatusModel', {
  task_id: types.optional(types.string, ''),
  meter: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
});

export interface IMeterActualStatus extends Instance<
  typeof MeterActualStatusModel
> {}

const ModalModel = types.model('ModalModel', {
  name: types.optional(types.maybeNull(ModalNamesEnum), null),
  isOpen: types.optional(types.boolean, false),
});

const AutomationModalsModel = types
  .model('AutomationModalsModel', {
    activeModals: types.optional(types.map(ModalModel), {}),
  })
  .actions((self) => ({
    openModal(name: ModalNames) {
      self.activeModals.set(name, { name, isOpen: true });
    },
    closeModal(name: ModalNames) {
      self.activeModals.delete(name);
    },
    closeAllModal() {
      self.activeModals.forEach((modal) => {
        modal.isOpen = false;
      });
    },
  }));

const MeltDownMeterData = types.model({
  M2: types.optional(types.number, 0),
  Gcon: types.optional(types.number, 0),
  T1: types.optional(types.number, 0),
  T2: types.optional(types.number, 0),
  dM_2: types.optional(types.number, 0),
  T3: types.optional(types.number, 0),
  Q: types.optional(types.number, 0),
});

const MeltDownMeterDifference = types.model({
  M2_excess: types.optional(types.number, 0),
  T1_excess: types.optional(types.number, 0),
  T2_excess: types.optional(types.number, 0),
  dM_2_excess: types.optional(types.number, 0),
  T3_excess: types.optional(types.number, 0),
  Q_excess: types.optional(types.number, 0),
});

const MeltDownMeterRequests = types
  .model({
    new: types.optional(types.number, 0),
    wip: types.optional(types.number, 0),
    finished: types.optional(types.number, 0),
  })
  .actions((self) => ({
    increaseNew() {
      self.new += 1;
    },
  }));

const MeltDownMeterModel = types.model({
  meter_id: types.optional(types.string, ''),
  meter_name: types.optional(types.string, ''),
  serial_number: types.optional(types.string, ''),
  data: types.optional(MeltDownMeterData, {}),
  difference_data: types.optional(MeltDownMeterDifference, {}),
  requests: types.optional(MeltDownMeterRequests, {}),
});

export interface IMeltDownMeterModel extends Instance<
  typeof MeltDownMeterModel
> {}

const MeltDownHouseModel = types.model('MeltDownModel', {
  house_id: types.optional(types.string, ''),
  meters: types.optional(types.array(MeltDownMeterModel), []),
  temperature: types.optional(types.number, 0),
});

export interface IMeltDownHouseModel extends Instance<
  typeof MeltDownHouseModel
> {}

const CheckedMeltMetersModel = types.model('CheckedMeltMetersModel', {
  house_id: types.optional(types.string, ''),
  meters: types.optional(types.array(types.string), []),
});

export interface ICheckedMeltMetersModel extends Instance<
  typeof CheckedMeltMetersModel
> {}

export const AutomationStore = types
  .model({
    statuses: types.optional(StatusesModel, {}),
    modals: types.optional(AutomationModalsModel, {}),
    selectedDate: types.optional(types.string, moment().toISOString()),
    allMetersReportStatus: types.optional(RequestStateModel, {}),
    housesTaskReport: types.optional(types.array(HouseReportModel), []),
    heatReportParams: types.optional(HeatReportParamsModel, {}),
    heatReport: types.optional(HeatReportModel, {}),
    submitHeatReportList: types.optional(
      types.array(SubmitHeatReportModel),
      []
    ),
    simAdapters: types.optional(types.array(SimAdapterModel), []),
    resourcesSettings: types.optional(ResourcesSettingsModel, {}),
    metersData: types.optional(
      types.frozen<{ [key: string]: IMeterData }>(),
      {}
    ),
    metersActualStatuses: types.optional(
      types.array(MeterActualStatusModel),
      []
    ),
    tempCorrection: types.optional(TempCorrectionModel, {}),
    requestHistory: types.optional(RequestHistoryModel, {}),
    meltDownReportDate: types.optional(types.string, ''),
    meltDownReport: types.optional(types.array(MeltDownHouseModel), []),
    checkedMeltMeters: types.optional(CheckedMeltMetersModel, {}),
  })
  .views((self) => ({
    get isHeatReportAvailable(): boolean {
      return (
        self.heatReport.isReportGenerated && !self.statuses.heatReport.pending
      );
    },
    get isDisableCommonMeterRequest(): boolean {
      return (
        self.allMetersReportStatus.pending ||
        self.statuses.housesTaskReport.pending ||
        !self.housesTaskReport.length
      );
    },
    get sortedHousesByCommonRespondedStatus(): IHouseReport[] {
      const sortedOrder: TaskReportDataStatuses[] = [
        'none',
        'partly',
        'offline',
        'full',
      ];

      const housesGroupedByCommonDataRespondedStatus = groupBy(
        self.housesTaskReport,
        (house) => {
          return house.commonDataRespondedStatus;
        }
      );

      return flatMap(
        sortedOrder,
        (key: TaskReportDataStatuses) =>
          housesGroupedByCommonDataRespondedStatus[key]
      ).filter((item) => item);
    },
    get currentMeterLastCheckout(): string | null {
      return self.metersData[self.heatReportParams.selectedMeter]
        ?.last_checkout[self.heatReportParams.reportType];
    },
    getMeterActualStatus(
      id: string,
      type: 'meter' | 'task'
    ): string | undefined {
      return self.metersActualStatuses.find(
        (meter) => meter[type === 'meter' ? 'meter' : 'task_id'] === id
      )?.status;
    },
  }))
  .actions((self) => ({
    clearAllData() {
      applySnapshot(self, {});
    },
    clearSubmitReportList() {
      applySnapshot(self.submitHeatReportList, []);
      self.statuses.submitHeatReport.clearStates();
    },
    clearCurrentHeatReport() {
      applySnapshot(self.heatReport, {});
      self.statuses.heatReport.clearStates();
    },
    setSelectedDate(ISODate: string) {
      self.selectedDate = ISODate;
    },
    setMetersActualStatuses(status: IMeterActualStatus) {
      self.metersActualStatuses = cast([
        ...self.metersActualStatuses.filter(
          (meter: IMeterActualStatus) => meter.meter !== status.meter
        ),
        status,
      ]);
    },
    toggleCheckMeltMeters(house_id: string, ids: string[]) {
      const meters = ids.every((id: string) =>
        self.checkedMeltMeters.meters.includes(id)
      )
        ? self.checkedMeltMeters.meters.filter(
            (checkedId: string) => !ids.includes(checkedId)
          )
        : uniq([...self.checkedMeltMeters.meters, ...ids]);

      if (!meters.length) {
        house_id = '';
      }

      self.checkedMeltMeters = cast({
        house_id,
        meters,
      });
    },
    getHousesTaskReport: flow(function* (date: string) {
      try {
        self.statuses.housesTaskReport.loadingStates();
        const response = yield Automation.GetHousesTaskReport({ date });
        self.housesTaskReport = response.results;
        self.statuses.housesTaskReport.successStates();

        return self.housesTaskReport;
      } catch (e) {
        self.statuses.housesTaskReport.errorStates();
        console.error(e);
      } finally {
        self.statuses.housesTaskReport.finallyStates();
      }
    }),
    postMeterTaskReport: flow(function* (
      meters: string[],
      forced: boolean,
      dataPreset?: 'automationResult' | 'heatConsumption' | 'none',
      data?: {
        report_type: string;
        date_from?: string;
        date_till?: string;
      }
    ) {
      try {
        let params: IObject = {
          meters,
          fixed_period: forced,
        };

        if (!forced) {
          params = Object.assign(params, {
            report_type:
              dataPreset === 'heatConsumption'
                ? self.heatReportParams.reportType
                : 'daily',
          });
        } else {
          switch (dataPreset) {
            case 'automationResult':
              params = Object.assign(params, {
                report_type: 'daily',
                // Нет уверенности и чётких требований в ТЗ, однако по логике тут должно быть fixed_period: true,
                // ведь нет опций запроса last_checkout для нескольких счётчиков,
                // как и нет возможности указывать в данном запросе различные даты для каждого счётчика.
                fixed_period: true,
                date_from: formatDateToISO(
                  moment(self.selectedDate).set({
                    date: 1,
                    hour: 0,
                    minute: 0,
                    second: 0,
                  })
                ),
                date_till: formatDateToISO(
                  moment(self.selectedDate)
                    .subtract(1, 'day')
                    .set({ hour: 23, minutes: 59, second: 59 })
                ),
              });
              break;
            case 'heatConsumption':
              const selectedDate = moment(self.heatReportParams.dateFrom);

              params = Object.assign(params, {
                date_from: formatDateToISO(moment(selectedDate)),
              });

              params = Object.assign(params, {
                report_type: self.heatReportParams.reportType,
                date_till: formatDateToISO(
                  moment(self.heatReportParams.dateTill)
                ),
              });

              if (
                params?.report_type === 'daily' &&
                !!params?.date_from &&
                !!params?.date_till
              ) {
                params.date_from = formatDateToISO(
                  moment(params.date_from).set({
                    hour: 0,
                    minute: 0,
                    second: 0,
                  })
                );
                params.date_till = formatDateToISO(
                  moment(params.date_till).set({
                    hour: 23,
                    minute: 59,
                    second: 59,
                  })
                );
              }

              break;
            default:
              params = Object.assign(params, data);
          }
        }

        self.statuses.metersReports.loadingStates();
        const response: { errors: string[]; tasks_id: (string | null)[] } =
          yield Automation.PostMeterTaskReport(params);
        self.statuses.metersReports.successStates();

        if (response.errors.length) {
          throw response.errors;
        }

        return response;
      } catch (e) {
        self.statuses.metersReports.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.metersReports.finallyStates();
      }
    }),
    getHeatReport: flow(function* (
      params: {
        meter: string;
        report_type: string;
        date_from: string;
        date_till: string;
      },
      isRecalculate?: boolean
    ) {
      try {
        self.statuses[
          isRecalculate ? 'recalculateReportLine' : 'heatReport'
        ].loadingStates();
        const response: IHeatReport = yield Automation.GetHeatReport(params);
        self.heatReport = response;
        self.statuses[
          isRecalculate ? 'recalculateReportLine' : 'heatReport'
        ].successStates();

        return response;
      } catch (e) {
        self.statuses[
          isRecalculate ? 'recalculateReportLine' : 'heatReport'
        ].errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses[
          isRecalculate ? 'recalculateReportLine' : 'heatReport'
        ].finallyStates();
      }
    }),
    getSubmitHeatReports: flow(function* (params: {
      meter: string;
      limit?: number;
      offset?: number;
    }) {
      try {
        self.statuses.submitHeatReportList.loadingStates();
        const response: { results: ISubmitHeatReport[] } =
          yield Automation.GetSubmitHeatReport(killNulls(params));
        self.submitHeatReportList = cast(response.results);
        self.statuses.submitHeatReportList.successStates();

        return response.results;
      } catch (e) {
        self.statuses.submitHeatReportList.errorStates();
        console.error(e);
      } finally {
        self.statuses.submitHeatReportList.finallyStates();
      }
    }),
    deleteSubmitHeatReport: flow(function* (meterId: string) {
      try {
        return yield Automation.DeleteSubmitHeatReport(meterId);
      } catch (e) {
        console.error(e);
      }
    }),
    getTemperatureCorrection: flow(function* (params: {
      date: string;
      house: string;
    }) {
      try {
        self.statuses.temperatureCorrection.loadingStates();
        const response: any = yield Automation.GetTemperatureCorrection(params);

        // Если корректировка не была никогда прежде указана, то ответом будет message: 'нет значения'.
        // В противном случае приходит ожидаемый согласно типизации результат.
        // Может пофиксят на бэке, а может и нет, пока что с проверкой.
        if (typeof response?.value === 'number') {
          self.tempCorrection = response;
        }

        self.statuses.temperatureCorrection.successStates();

        return response;
      } catch (e) {
        self.statuses.temperatureCorrection.errorStates();
        console.error(e);
      } finally {
        self.statuses.temperatureCorrection.finallyStates();
      }
    }),
    saveTemperatureCorrection: flow(function* (data: {
      house: string;
      date: string;
      value: number;
      comment?: string;
    }) {
      try {
        self.statuses.temperatureCorrection.loadingStates();
        const response: IObject = yield Automation.PostTemperatureCorrection(
          killNulls(data)
        );
        self.statuses.temperatureCorrection.successStates();

        return response;
      } catch (e) {
        self.statuses.temperatureCorrection.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.temperatureCorrection.finallyStates();
      }
    }),
    getSimAdapters: flow(function* (house: string, meter?: string) {
      try {
        self.statuses.simAdapters.loadingStates();
        const response: IObject = yield Automation.GetAdapterSim(
          killNulls({ house, meter })
        );
        self.simAdapters = response.results;
        self.statuses.simAdapters.successStates();

        return response.results;
      } catch (e) {
        self.statuses.simAdapters.errorStates();
        console.error(e);
      } finally {
        self.statuses.simAdapters.finallyStates();
      }
    }),
    getLastTasks: flow(function* (
      meters: string[],
      last_tasks: boolean = true,
      last_checkout: boolean = true
    ) {
      try {
        self.statuses.lastTasks.loadingStates();
        const response: IObject = yield Automation.GetLastTasks({
          meters,
          last_tasks,
          last_checkout,
        });
        self.metersData = response.meters_data;
        self.statuses.lastTasks.successStates();

        return response.meters_data;
      } catch (e) {
        console.error(e);
        self.statuses.lastTasks.errorStates();
      } finally {
        self.statuses.lastTasks.finallyStates();
      }
    }),
    updateReportLine: flow(function* (
      id: string,
      params: { heat_system_name: string; row_data: IObject }
    ) {
      try {
        self.statuses.updateReportLine.loadingStates();
        const response = yield Automation.EditLineHeatReport(id, params);
        self.statuses.updateReportLine.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.updateReportLine.errorStates();
      } finally {
        self.statuses.updateReportLine.finallyStates();
      }
    }),
    recalculateReportLine: flow(function* (
      id: string,
      type: 'average' | 'arithmetic'
    ) {
      try {
        self.statuses.recalculateReportLine.loadingStates();
        let response: any;

        if (type === 'average') {
          response = yield Automation.AverageLineHeatReport(id);
        } else {
          response = yield Automation.ArithmeticLineHeatReport(id);
        }

        self.statuses.recalculateReportLine.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.recalculateReportLine.errorStates();
        // throw e;
      } finally {
        self.statuses.recalculateReportLine.finallyStates();
      }
    }),
    postRecalculateMeter: flow(function* (data: {
      meter: string;
      date_from?: string;
      date_till?: string;
      report_type?: string;
    }) {
      try {
        self.statuses.recalculateMeter.loadingStates();
        const response = yield Automation.PostMeterRecalculate(killNulls(data));
        self.statuses.recalculateMeter.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.recalculateMeter.errorStates();
      } finally {
        self.statuses.recalculateMeter.finallyStates();
      }
    }),
    submitHeatReport: flow(function* (data: {
      meter: string;
      report_type: string;
      date_from: string;
      date_till: string;
      temperature: number;
    }) {
      try {
        self.statuses.submitHeatReport.loadingStates();
        const response = yield Automation.PostHeatReport(data);
        self.statuses.submitHeatReport.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.submitHeatReport.errorStates();
      } finally {
        self.statuses.submitHeatReport.finallyStates();
      }
    }),
    getMeltDownReport: flow(function* (date: string) {
      try {
        self.statuses.meltDownReport.loadingStates();
        const response = yield Automation.GetHeatHunter({ date });
        self.statuses.meltDownReport.successStates();
        self.meltDownReportDate = response.date;
        self.meltDownReport = response.houses;

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.meltDownReport.errorStates();
      } finally {
        self.statuses.meltDownReport.finallyStates();
      }
    }),
    saveCreateRequestReport: flow(function* (data: {
      house: string;
      request: { id: string };
      meters_ids: string[];
    }) {
      try {
        return Automation.CreateRequestReport(data);
      } catch (e) {
        console.error(e);
      }
    }),
  }));
